/**
 * Documents, folders, attachments, and the photo gallery.
 */

import { request } from './client.js';
import { pageCall, type RegisterPage } from './paging.js';

// -------------------------------------------------------------------- documents

export interface DocumentRow {
  readonly id: string;
  readonly project_id: string | null;
  readonly folder_id: string | null;
  /** Where it is filed, in words — null when loose. */
  readonly folder_path: string | null;
  /** True when the FOLDER shares it — the screen says which act shared a file. */
  readonly folder_external_visible: boolean | null;
  readonly filename: string;
  readonly mime_type: string;
  readonly size_bytes: string;
  readonly category: string;
  readonly version_no: number;
  readonly is_external_visible: boolean;
  readonly scan_status: string;
  /**
   * Whether the bytes may actually be fetched, decided by the SERVER.
   *
   * `scan_status` alone cannot answer it: the malware policy depends on whether
   * that server has a scanner configured, which the browser has no way to know.
   * Offering a download link the server then refuses is the Day 17 bug — an
   * action its reader cannot take — so the row asks this, never the status.
   */
  readonly can_download: boolean;
  readonly uploaded_by_name: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface Folder {
  readonly id: string;
  readonly project_id: string | null;
  readonly parent_id: string | null;
  readonly name: string;
  readonly is_external_visible: boolean;
  readonly path: string;
  readonly document_count: number;
}

export interface DocumentVersion {
  readonly id: string;
  readonly version_no: number;
  readonly filename: string;
  readonly size_bytes: string;
  readonly note: string | null;
  readonly uploaded_by_name: string | null;
  readonly uploaded_at: string;
}

export type DocumentDetail = DocumentRow & { readonly versions: readonly DocumentVersion[] };

export interface Attachment {
  readonly id: string;
  readonly document_id: string;
  readonly filename: string;
  readonly mime_type: string;
  readonly size_bytes: string;
  readonly caption: string | null;
  readonly is_external_visible: boolean;
  readonly attached_by_name: string | null;
  readonly created_at: string;
  readonly scan_status: string;
  /** Same rule as `DocumentRow.can_download` — the SERVER decides. See there. */
  readonly can_download: boolean;
}

export const DOCUMENT_CATEGORIES = [
  'drawing',
  'photo',
  'certificate',
  'method_statement',
  'correspondence',
  'contract',
  'invoice',
  'report',
  'other',
] as const;

/**
 * The register, which the server bounds.
 *
 * The total counts what THIS reader may see — the server applies the
 * external-visibility rule to the count as well as the rows, so a client is
 * never told there are files they cannot open. See `api/paging.ts`.
 */
export function listDocuments(
  projectId: string,
  category?: string,
  /** A folder id, or 'none' for loose files only. Absent = everything. */
  folder?: string,
  limit?: number,
): Promise<RegisterPage<DocumentRow>> {
  const params = new URLSearchParams();
  if (category) params.set('category', category);
  if (folder) params.set('folder', folder);
  const query = params.toString() ? `?${params.toString()}` : '';
  return pageCall<DocumentRow>(
    `/v1/projects/${encodeURIComponent(projectId)}/documents${query}`,
    limit,
  );
}

const foldersPath = (projectId: string): string =>
  `/v1/projects/${encodeURIComponent(projectId)}/folders`;

export async function listFolders(projectId: string): Promise<readonly Folder[]> {
  return (await request<{ data: readonly Folder[] }>(foldersPath(projectId))).data;
}

export async function createFolder(
  projectId: string,
  input: { name: string; parent_id?: string | null },
): Promise<readonly Folder[]> {
  return (
    await request<{ data: readonly Folder[] }>(foldersPath(projectId), {
      method: 'POST',
      body: JSON.stringify(input),
    })
  ).data;
}

export async function updateFolder(
  projectId: string,
  folderId: string,
  input: { name?: string; is_external_visible?: boolean },
): Promise<readonly Folder[]> {
  return (
    await request<{ data: readonly Folder[] }>(
      `${foldersPath(projectId)}/${encodeURIComponent(folderId)}`,
      { method: 'PATCH', body: JSON.stringify(input) },
    )
  ).data;
}

export async function deleteFolder(
  projectId: string,
  folderId: string,
): Promise<readonly Folder[]> {
  return (
    await request<{ data: readonly Folder[] }>(
      `${foldersPath(projectId)}/${encodeURIComponent(folderId)}`,
      { method: 'DELETE' },
    )
  ).data;
}

export function moveDocumentToFolder(
  projectId: string,
  documentId: string,
  folderId: string | null,
): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(
    `/v1/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(documentId)}/folder`,
    { method: 'PATCH', body: JSON.stringify({ folder_id: folderId }) },
  );
}

export function getDocument(projectId: string, documentId: string): Promise<DocumentDetail> {
  return request<DocumentDetail>(
    `/v1/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(documentId)}`,
  );
}

/**
 * Upload the file as the RAW BODY, with the metadata in the query string.
 *
 * Not `FormData`: the server takes `application/octet-stream` and validates the
 * query with the same schema machinery as any other input, so there is no
 * multipart parser on either side. See the content-type parser in `server.ts`.
 */
export function uploadDocument(
  projectId: string,
  file: File,
  options: {
    category?: string;
    external?: boolean;
    folderId?: string;
    /**
     * What the file is evidence FOR. The server writes the link in the same
     * transaction as the file, so a caller passing this never has to handle
     * "the file saved and the link did not" — that state cannot occur.
     */
    attachTo?: { entityType: string; entityId: string };
  } = {},
): Promise<DocumentRow> {
  const params = new URLSearchParams({
    filename: file.name,
    // Browsers occasionally report nothing for an unusual extension. Sending a
    // blank would fail the schema with a confusing message, so name it and let
    // the allow-list produce the sentence that actually helps.
    mime_type: file.type || 'application/octet-stream',
  });
  if (options.category) params.set('category', options.category);
  if (options.external) params.set('external', 'true');
  if (options.folderId) params.set('folder_id', options.folderId);
  if (options.attachTo) {
    params.set('attach_entity_type', options.attachTo.entityType);
    params.set('attach_entity_id', options.attachTo.entityId);
  }

  return request<DocumentRow>(
    `/v1/projects/${encodeURIComponent(projectId)}/documents?${params.toString()}`,
    { method: 'POST', body: file, headers: { 'content-type': 'application/octet-stream' } },
  );
}

export function addDocumentVersion(
  projectId: string,
  documentId: string,
  file: File,
  note?: string,
): Promise<DocumentDetail> {
  const params = new URLSearchParams({
    filename: file.name,
    mime_type: file.type || 'application/octet-stream',
  });
  if (note) params.set('note', note);

  return request<DocumentDetail>(
    `/v1/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(documentId)}/versions?${params.toString()}`,
    { method: 'POST', body: file, headers: { 'content-type': 'application/octet-stream' } },
  );
}

export function updateDocument(
  projectId: string,
  documentId: string,
  input: { filename?: string; category?: string; is_external_visible?: boolean },
): Promise<DocumentRow> {
  return request<DocumentRow>(
    `/v1/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(documentId)}`,
    { method: 'PATCH', body: JSON.stringify(input) },
  );
}

export async function deleteDocument(projectId: string, documentId: string): Promise<void> {
  await request<void>(
    `/v1/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(documentId)}`,
    { method: 'DELETE' },
  );
}

/** A real link, so the browser downloads and names the file itself. */
export function documentFileUrl(
  projectId: string,
  documentId: string,
  versionNo?: number,
  /** `inline` renders in the viewer (PDF/images only; the server decides). */
  disposition?: 'inline',
): string {
  const params = new URLSearchParams();
  if (versionNo) params.set('version', String(versionNo));
  if (disposition) params.set('disposition', disposition);
  const query = params.toString() ? `?${params.toString()}` : '';
  return `/v1/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(documentId)}/file${query}`;
}

/** True when the viewer can show this type inline (DC-04). */
export function isViewableInline(mimeType: string): boolean {
  return ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'].includes(
    mimeType.toLowerCase().split(';')[0]!.trim(),
  );
}

export async function listAttachments(
  projectId: string,
  entityType: string,
  entityId: string,
): Promise<readonly Attachment[]> {
  return (
    await request<{ data: readonly Attachment[] }>(
      `/v1/projects/${encodeURIComponent(projectId)}/attachments/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`,
    )
  ).data;
}

export async function attachDocument(
  projectId: string,
  entityType: string,
  entityId: string,
  input: { document_id: string; caption?: string },
): Promise<readonly Attachment[]> {
  return (
    await request<{ data: readonly Attachment[] }>(
      `/v1/projects/${encodeURIComponent(projectId)}/attachments/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`,
      { method: 'POST', body: JSON.stringify(input) },
    )
  ).data;
}

export async function detachDocument(projectId: string, attachmentId: string): Promise<void> {
  await request<void>(
    `/v1/projects/${encodeURIComponent(projectId)}/attachments/${encodeURIComponent(attachmentId)}`,
    { method: 'DELETE' },
  );
}

// ---------------------------------------------------------------------------
// The photo gallery (ST-06)
// ---------------------------------------------------------------------------

export interface PhotoRow {
  readonly attachment_id: string;
  readonly document_id: string;
  readonly filename: string;
  readonly size_bytes: string;
  readonly is_external_visible: boolean;
  readonly scan_status: string;
  /** Same rule as `DocumentRow.can_download` — the SERVER decides. See there. */
  readonly can_download: boolean;
  readonly attached_at: string;
  readonly attached_by_name: string | null;
  readonly entity_type: string;
  readonly entity_id: string;
  readonly entity_label: string | null;
  readonly location_path: string | null;
  readonly trade_name: string | null;
  /** The day the photo is ABOUT — the diary's day where there is one. */
  readonly day: string;
}

export async function listPhotos(projectId: string): Promise<readonly PhotoRow[]> {
  const body = await request<{ data: readonly PhotoRow[] }>(
    `/v1/projects/${encodeURIComponent(projectId)}/photos`,
  );
  return body.data;
}
